import React, { useEffect } from 'react';

/**
 * The portal's modal, on the house `.ps-modal` surface.
 *
 * WHY THIS EXISTS. `.ps-modal` / `.ps-modal-overlay` have been in
 * portalSurface.css for a while, but every caller hand-rolled the behaviour:
 * SlotEditorTab, RegistrationTab, Students and TodayPage each write their own
 * `{show && <div className="ps-modal-overlay" onClick={close}>` with a
 * `stopPropagation` on the panel. That was survivable at two copies. The
 * React-Bootstrap conversion added two more on Email Templates and one on
 * Manage Announcements, and duplicating a behaviour five ways is how one copy
 * quietly loses a piece of it — which is exactly what happened: NONE of the
 * hand-rolled copies closes on ESC, a behaviour Bootstrap's <Modal> gave for
 * free and which the conversion would otherwise have silently dropped.
 *
 * WHAT IT DOES: overlay + centred panel, backdrop click to close, ESC to close,
 * `stopPropagation` so a click inside the panel does not, and dialog semantics
 * (`role="dialog"`, `aria-modal`, an accessible name from `title`).
 *
 * WHAT IT DELIBERATELY DOES NOT DO: trap focus, or restore focus to the trigger
 * on close. No portal modal has ever done either. Centralising the pattern is
 * what makes fixing that a single change later instead of five; it is NOT fixed
 * here, and this comment exists so the gap is recorded rather than assumed
 * handled.
 */
const PsModal = ({
	open,
	onClose,
	title,
	titleIcon,
	className = '',
	width,
	footer,
	children,
}) => {
	useEffect(() => {
		if (!open) return undefined;
		const onKeyDown = (e) => {
			if (e.key === 'Escape') onClose();
		};
		// Bound to document: focus may sit inside any field in the panel.
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [open, onClose]);

	if (!open) return null;

	return (
		<div className='ps-modal-overlay' onClick={onClose}>
			<div
				className={`ps-modal ${className}`.trim()}
				role='dialog'
				aria-modal='true'
				aria-label={title}
				style={width ? { maxWidth: width } : undefined}
				onClick={(e) => e.stopPropagation()}>
				{title && (
					<h3 className='ps-modal__title'>
						{titleIcon && (
							<>
								<i className={`fa-sharp fa-solid ${titleIcon}`} aria-hidden='true' />{' '}
							</>
						)}
						{title}
					</h3>
				)}
				{children}
				{footer && <div className='ps-modal__footer'>{footer}</div>}
			</div>
		</div>
	);
};

export default PsModal;
